"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

function AnimatedNumber({ target, suffix = "" }: { target: number; suffix?: string }) {
	const [val, setVal] = useState(0);
	const ref = useRef<HTMLSpanElement>(null);
	const started = useRef(false);

	useEffect(() => {
		if (!ref.current) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !started.current) {
					started.current = true;
					const step = Math.ceil(target / 40);
					const id = setInterval(() => {
						setVal((v) => {
							if (v + step >= target) {
								clearInterval(id);
								return target;
							}
							return v + step;
						});
					}, 30);
				}
			},
			{ threshold: 0.3 },
		);
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [target]);

	return (
		<span ref={ref} className="tabular-nums">
			{val}
			{suffix}
		</span>
	);
}

const STATS = [
	{ key: "factories", target: 100, suffix: "+" },
	{ key: "newDesigns", target: 500, suffix: "+" },
	{ key: "afterSales", target: 180, suffix: " Day" },
	{ key: "workers", target: 200, suffix: "+" },
];

const SOLUTIONS = [
	{ key: "distributor", accent: "oklch(0.7 0.17 76)" },
	{ key: "brand", accent: "oklch(0.62 0.16 72)" },
	{ key: "designer", accent: "oklch(0.75 0.14 82)" },
	{ key: "retail", accent: "oklch(0.66 0.17 74)" },
];

export default function StatsAndSolutions() {
	const t = useTranslations("homeBeauty");
	const [hoveredSol, setHoveredSol] = useState<number | null>(null);

	return (
		<>
			{/* stats strip */}
			<section className="w-full bg-brand-tint py-10 md:py-14">
				<div className="mx-auto grid w-full max-w-[1580px] grid-cols-2 gap-6 px-5 sm:px-8 md:grid-cols-4 lg:px-12">
					{STATS.map((s) => (
						<div key={s.key} className="flex flex-col items-center text-center">
							<span className="font-display text-[clamp(36px,3.5vw,62px)] leading-none text-brand">
								<AnimatedNumber target={s.target} suffix={s.suffix} />
							</span>
							<span className="mt-2 font-body text-[12px] uppercase tracking-[0.18em] text-brand-muted xl:text-[13px]">
								{t(`stat_${s.key}`) || s.key}
							</span>
						</div>
					))}
				</div>
			</section>

			{/* solutions */}
			<section className="w-full bg-white py-16 sm:py-20 md:py-24">
				<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
					<header className=" text-center">
						<span className="inline-flex items-center gap-2 font-body text-[12px] font-medium uppercase tracking-[0.2em] text-brand-fg xl:text-[13px]">
							<span aria-hidden className="inline-block h-px w-8 bg-brand" />
							{t("solEyebrow") || "Solutions"}
							<span aria-hidden className="inline-block h-px w-8 bg-brand" />
						</span>
						<h2 className="mt-4 font-display text-[clamp(30px,3.25vw,54px)] leading-[1.12] text-brand-ink">
							{t("solTitle") || "Tailored Solutions for Your Business"}
						</h2>
						<p className="mx-auto mt-4 max-w-[52ch] font-body text-[16px] leading-[1.72] text-brand-muted lg:text-[17px]">
							{t("solDesc") ||
								"Professional beauty equipment supplier with 10+ years of expertise, offering factory-direct pricing and comprehensive after-sales support."}
						</p>
					</header>

					<div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{SOLUTIONS.map((sol, i) => {
							const isHovered = hoveredSol === i;
							return (
								<article
									key={sol.key}
									className="group relative flex flex-col overflow-hidden bg-brand-tint p-6 transition-all duration-500 hover:bg-brand-ink lg:p-7"
									onMouseEnter={() => setHoveredSol(i)}
									onMouseLeave={() => setHoveredSol(null)}
								>
									<span
										className="mb-4 inline-flex h-10 w-10 items-center justify-center font-display text-[20px] transition-colors duration-300 group-hover:text-white xl:text-[21px]"
										style={{ color: sol.accent }}
									>
										0{i + 1}
									</span>
									<h3 className="font-display text-[22px] leading-[1.2] text-brand-ink transition-colors duration-300 group-hover:text-white lg:text-[24px] xl:text-[25px]">
										{t(`sol_${sol.key}_title`) || sol.key}
									</h3>
									<p className="mt-3 font-body text-[15px] leading-[1.68] text-brand-muted transition-colors duration-300 group-hover:text-white/65">
										{t(`sol_${sol.key}_question`) || ""}
									</p>
									<p className="mt-2 font-body text-[14px] leading-[1.62] text-brand-ink/50 transition-colors duration-300 group-hover:text-white/45">
										{t(`sol_${sol.key}_answer`) || ""}
									</p>
									<span
										aria-hidden
										className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
										style={{ backgroundColor: sol.accent }}
									/>
								</article>
							);
						})}
					</div>
				</div>
			</section>
		</>
	);
}
